import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import {
  AccountTreeOutlined,
  ArrowForward,
  BadgeOutlined,
  BlockOutlined,
  CalculateOutlined,
  CategoryOutlined,
  ChevronLeft,
  ChevronRight,
  CodeOutlined,
  CurrencyRupeeOutlined,
  DescriptionOutlined,
  EditOutlined,
  EventBusyOutlined,
  EventRepeatOutlined,
  FileDownloadOutlined,
  FunctionsOutlined,
  GroupsOutlined,
  HistoryOutlined,
  LockOutlined,
  NotesOutlined,
  PaymentsOutlined,
  PercentOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
  PersonOutlineOutlined,
  ReceiptLongOutlined,
  RestartAltOutlined,
  TagOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { Loader } from "components/Loader";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { tooltipStyleProps } from "services/constants/Styles";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { filterToolbarButtonSx } from "components/CustomComponents/filterToolbarStyles";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { GlobalConfirmDialog, globalTextFieldSx } from "components/DesignSystem/sharedUiPatterns";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { PayInputAuditTrail } from "pages/PayRoll/PayInputs/Shared/AuditTrail";
import { SelectField, TextField, toOptions } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { CustomMultiSelectionWithLabel } from "components/CustomComponents/CustomMultiSelectionWithLabel";
import { bulkSelectionCheckboxSx } from "pages/TeamZone/Approve/approveUi";
import {
  CountPill,
  Required,
  EmptyRow,
  SimpleTable,
  multilineFieldSx,
  searchFieldSx,
  tableCellSx,
  DiffTable,
  FormulaEditor,
  MethodCards,
  PreviewBox,
  PreviewLine,
  SystemTag,
  ToneChip,
  VersionChip,
  YesNoSwitch,
} from "../Shared/salaryUi";
import {
  EDITABLE_METHODS,
  FORMULA_FUNCTIONS,
  PREVIEW_GROSS,
  COMPONENT_STATUS_COLORS,
  USER_CATEGORIES,
  baseCodes,
  changedVersionFields,
  checkFormula,
  componentStatus,
  currentVersion,
  describeCalculation,
  employeeCount,
  formToVersion,
  formatDay,
  formatRupees,
  formulaCodes,
  joinBase,
  percentBaseCodes,
  lastChanged,
  previewVersion,
  templateCount,
  validateComponentForm,
  versionFieldValue,
  versionLabel,
} from "./salaryComponentData";

const EMPLOYEES_PER_PAGE = 25;
const SALARY_TEMPLATES_PATH = "/admin/payroll/salary/salary-templates";

const isEditableMethod = (method) => EDITABLE_METHODS.some((item) => item.value === method);


// ---------------- View panel tabs ----------------

const { indigo, pink, blue, green, orange, purple } = detailAccentPalette;
const slate = { color: designColors.slate600, bg: designColors.slate100 };

// Header card at the top of the view, same look as the employee card on the
// Pay Inputs views (RequestDetailHeader): icon, name + status, then an
// inline Code | Category | Version strip.
const ComponentHeaderCard = ({ component }) => {
  const version = currentVersion(component);
  const status = componentStatus(component);
  const item = (value, withDivider) => (
    <Mui.Typography
      component="span"
      sx={{
        pl: withDivider ? 2 : 0,
        ml: withDivider ? 0.25 : 0,
        borderLeft: withDivider ? `1px solid ${designColors.slate200}` : "none",
        color: designColors.slate800,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Mui.Typography>
  );
  return (
    <Mui.Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        border: `1px solid ${designColors.slate200}`,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${designColors.tealTint} 0%, ${designColors.white} 100%)`,
        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <Mui.Stack direction="row" spacing={2} alignItems="center">
        <Mui.Avatar
          sx={{
            width: 56,
            height: 56,
            backgroundColor: component.active ? designColors.tealDark : designColors.slate400,
            color: designColors.white,
          }}
        >
          <PaymentsOutlined />
        </Mui.Avatar>
        <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
          <Mui.Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
            <Mui.Typography
              title={component.name}
              sx={{ fontSize: { xs: 17, sm: 18 }, fontWeight: 700, color: designColors.slate900, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {component.name}
            </Mui.Typography>
            <RequestStatusChip status={status} colorCode={COMPONENT_STATUS_COLORS[status]} />
            {/* The status chip already reads "System" for active system components. */}
            {component.system && status !== "System" ? <SystemTag /> : null}
          </Mui.Stack>
          <Mui.Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.2, rowGap: 1 }}>
            {item(component.code, false)}
            {item(component.category || "–", true)}
            {item(versionLabel(version.version), true)}
          </Mui.Stack>
        </Mui.Box>
      </Mui.Stack>
    </Mui.Box>
  );
};

const categoryHelper = (category) => {
  if (category === "Earnings") return "Counts towards GROSS";
  if (category === "Deductions") return "Taken off the pay";
  if (category === "Calculation") return "Not paid; used only inside other formulas";
  return undefined;
};

const calculationFields = (component, components) => {
  const version = currentVersion(component);
  if (!isEditableMethod(version.method)) {
    return [
      { icon: FunctionsOutlined, label: "Method", value: version.method, accent: purple, rightPill: versionLabel(version.version) },
      {
        icon: CalculateOutlined,
        label: "Amount",
        value: version.method === "Statutory" ? "Worked out by the statutory engine" : "Taken from approved pay inputs",
        accent: orange,
        multiline: true,
      },
    ];
  }
  const preview = previewVersion(version, components, PREVIEW_GROSS, component.code);
  return [
    { icon: FunctionsOutlined, label: "Method", value: version.method, accent: purple, rightPill: versionLabel(version.version) },
    ...(version.method === "Fixed Amount"
      ? [{ icon: CurrencyRupeeOutlined, label: "Amount", value: `${formatRupees(version.amount)} per pay period`, accent: green }]
      : []),
    ...(version.method === "Percentage"
      ? [{ icon: PercentOutlined, label: "Percentage", value: `${version.percentage}% of ${version.base}`, accent: green }]
      : []),
    ...(version.method === "Formula"
      ? [{
          icon: CodeOutlined,
          label: "Formula",
          value: version.formula,
          accent: green,
          multiline: true,
        }]
      : []),
    ...(version.method === "Fixed Amount"
      ? [{
          icon: EventRepeatOutlined,
          label: "Prorate for Paid Days",
          value: version.prorate ? "Yes" : "No",
          helper: version.prorate ? "Reduced for LOP and part periods" : "Paid in full whenever the employee is paid",
          accent: blue,
        }]
      : []),
    {
      icon: CalculateOutlined,
      label: `Preview at GROSS ${formatRupees(PREVIEW_GROSS)}`,
      value: `${component.code} = ${preview.amount == null ? "–" : formatRupees(preview.amount)}`,
      accent: orange,
    },
  ];
};

const DetailsTab = ({ component, components }) => {
  const status = componentStatus(component);
  const nextVersion = currentVersion(component).version + 1;
  const employees = employeeCount(component);
  const templates = templateCount(component);
  const changed = lastChanged(component);
  return (
    <Mui.Stack spacing={2}>
      {!component.active && employees ? (
        <RequestInfoBanner
          tone="warning"
          title={`Still paid to ${employees} employees through ${templates} templates.`}
          body="It can't be added to templates while inactive."
        />
      ) : null}
      {component.active && status === "Used" ? (
        <RequestInfoBanner
          tone="warning"
          title={`Used in ${templates} template${templates === 1 ? "" : "s"}.`}
          body={`Changing the category, method, value or pro-rata setting saves as ${versionLabel(nextVersion)} with a change note. Existing templates and employees keep their version. Name, payslip name, description and payslip display update in place.`}
        />
      ) : null}
      {component.system ? (
        <RequestInfoBanner
          title="System component"
          body="Added at payroll by Statutory or Pay Inputs. You can edit it like any other component; only the code is locked."
        />
      ) : null}

      <RequestDetailsSection
        title="COMPONENT DETAILS"
        fields={[
          { icon: TagOutlined, label: "Code", value: component.code, helper: "Locked: used in formulas", accent: indigo },
          { icon: CategoryOutlined, label: "Category", value: component.category || "–", helper: categoryHelper(component.category), accent: pink },
          { icon: TuneOutlined, label: "Type", value: component.system ? "System component" : "User component", accent: purple },
          { icon: ReceiptLongOutlined, label: "Display in Payslip", value: component.show_in_payslip ? "Yes" : "No", accent: blue },
          ...(component.show_in_payslip
            ? [{ icon: BadgeOutlined, label: "Name in Payslip", value: component.payslip_name || "Uses component name", accent: blue }]
            : []),
          ...(component.description
            ? [{ icon: NotesOutlined, label: "Description", value: component.description, accent: slate, multiline: true }]
            : []),
        ]}
      />

      <RequestDetailsSection title="CALCULATION" fields={calculationFields(component, components)} />

      <RequestDetailsSection
        title="USAGE"
        fields={[
          { icon: AccountTreeOutlined, label: "Used in Templates", value: String(templates), accent: indigo },
          { icon: GroupsOutlined, label: "Paid to Employees", value: String(employees), accent: green },
          {
            icon: HistoryOutlined,
            label: "Last Changed",
            value: `${formatDay(changed.at)}${changed.version ? ` · ${versionLabel(changed.version)}` : ""}`,
            accent: orange,
          },
        ]}
      />

      {!component.active && component.deactivation ? (
        <RequestDetailsSection
          title="DEACTIVATION"
          fields={[
            { icon: EventBusyOutlined, label: "Deactivated On", value: formatDay(component.deactivation.at), accent: slate },
            ...(component.deactivation.by ? [{ icon: PersonOutlineOutlined, label: "Deactivated By", value: component.deactivation.by, accent: slate }] : []),
            ...(component.deactivation.reason
              ? [{ icon: NotesOutlined, label: "Reason", value: component.deactivation.reason, accent: slate, multiline: true }]
              : []),
          ]}
        />
      ) : null}
    </Mui.Stack>
  );
};

const linkSx = {
  color: designColors.tealDark,
  fontWeight: designWeights.semibold,
  fontSize: 13,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  textAlign: "left",
};

const TemplatesTab = ({ component }) => {
  const history = useHistory();
  const [search, setSearch] = useState("");
  const current = currentVersion(component).version;
  const onCurrent = component.templates.filter((template) => template.uses_version === current).length;
  const canUpgrade = component.templates.length - onCurrent;
  const rows = component.templates.filter((template) => template.name.toLowerCase().includes(search.trim().toLowerCase()));
  const openTemplates = () => history.push(SALARY_TEMPLATES_PATH);
  return (
    <Mui.Stack spacing={2}>
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <CountPill label={`${onCurrent} on ${versionLabel(current)}`} tone="green" />
          {canUpgrade ? <CountPill label={`${canUpgrade} can upgrade to ${versionLabel(current)}`} tone="blue" /> : null}
        </Mui.Stack>
        <Mui.TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search templates"
          sx={{ ...searchFieldSx, width: { xs: "100%", sm: 240 } }}
        />
      </Mui.Stack>
      {/* Full values, no truncation: scrolls sideways when wider than the panel. */}
      <SimpleTable
        fit
        heads={[
          { label: "Template" },
          { label: "Pay Basis", width: 84 },
          { label: "Version", width: 72 },
          { label: `Uses ${component.code}`, width: 104 },
          { label: "Calculation" },
          { label: "Employees", align: "right", width: 88 },
        ]}
      >
        {rows.length ? (
          rows.map((template) => {
            const own = component.versions.find((item) => item.version === template.uses_version);
            const outdated = template.upgrade_available || template.uses_version !== current;
            return (
              <Mui.TableRow key={template.key}>
                <Mui.TableCell sx={tableCellSx}>
                  <Mui.Link component="button" type="button" onClick={openTemplates} underline="hover" sx={{ ...linkSx, fontSize: 14 }}>
                    {template.name}
                  </Mui.Link>
                </Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>{template.pay_basis || "–"}</Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}><VersionChip label={versionLabel(template.template_version)} /></Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>
                  <Mui.Stack spacing={0.5} alignItems="flex-start">
                    <VersionChip label={versionLabel(template.uses_version)} current={!outdated} />
                    {outdated ? <ToneChip label={`${versionLabel(current)} available`} tone="blue" /> : null}
                    {outdated ? (
                      <Mui.Link component="button" type="button" onClick={openTemplates} underline="hover" sx={linkSx}>
                        Upgrade <ArrowForward sx={{ fontSize: 14 }} />
                      </Mui.Link>
                    ) : null}
                  </Mui.Stack>
                </Mui.TableCell>
                <Mui.TableCell sx={tableCellSx}>
                  <Mui.Stack spacing={0.5} alignItems="flex-start">
                    <span>{template.calc_override || (own ? describeCalculation(own) : "–")}</span>
                    {template.calc_override ? <ToneChip label="Overridden" tone="purple" /> : null}
                  </Mui.Stack>
                </Mui.TableCell>
                <Mui.TableCell align="right" sx={tableCellSx}>{template.employees.toLocaleString("en-IN")}</Mui.TableCell>
              </Mui.TableRow>
            );
          })
        ) : (
          <EmptyRow colSpan={6} text={component.templates.length ? "No templates match your search." : "No template uses this component yet."} />
        )}
      </SimpleTable>
      <RequestInfoBanner
        body={`Templates pin a component version. Upgrading a template to ${versionLabel(current)} creates a new template version. Employees move to it only through a Template-Based Revision.`}
      />
    </Mui.Stack>
  );
};

const useEmployeeFilters = (component) => {
  const [search, setSearch] = useState("");
  const [template, setTemplate] = useState("");
  const [version, setVersion] = useState("");
  const [page, setPage] = useState(0);
  useEffect(() => {
    setSearch(""); setTemplate(""); setVersion(""); setPage(0);
  }, [component.id]);
  useEffect(() => setPage(0), [search, template, version]);
  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return component.employees.filter(
      (row) =>
        (!query || row.name.toLowerCase().includes(query) || row.employee_code.toLowerCase().includes(query)) &&
        (!template || row.template === template) &&
        (!version || versionLabel(row.version) === version),
    );
  }, [component.employees, search, template, version]);
  return { search, setSearch, template, setTemplate, version, setVersion, page, setPage, rows };
};

const EXPORT_FORMATS = [
  { value: "excel", label: "Excel", icon: TableChartOutlined },
  { value: "pdf", label: "PDF", icon: PictureAsPdfOutlined },
  { value: "csv", label: "CSV", icon: DescriptionOutlined },
];

// Download menu for the Employees tab, same Excel / PDF / CSV choice as the
// list screen. Exports every row matching the filters, not just this page.
// Nine data columns so the PDF (max 10 with S No) fits.
const EmployeesDownload = ({ component, rows, filters }) => {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const writers = useExcelDownload();
  const pers = [...new Set(rows.map((row) => row.per).filter(Boolean))];
  const singlePer = pers.length <= 1;
  const headers = [
    { name: "Employee", key: "name" },
    { name: "Employee Code", key: "employee_code" },
    { name: "Department", key: "department" },
    { name: "Template", key: "template" },
    { name: "Structure", key: "structure" },
    { name: "Version", key: "version" },
    { name: "Calculation", key: "calculation" },
    { name: singlePer && pers[0] ? `Amount (per ${pers[0]})` : "Amount", key: "amount" },
    { name: "Effective From", key: "effective_from" },
  ];
  const download = async (format) => {
    setAnchor(null);
    if (busy || !rows.length) return;
    setBusy(true);
    try {
      const data = rows.map((row) => ({
        name: row.name || "–",
        employee_code: row.employee_code || "–",
        department: row.department || "–",
        template: row.template || "–",
        structure: row.customized ? "Customized" : "Template Linked",
        version: versionLabel(row.version),
        calculation: row.calculation || "–",
        amount: row.amount == null ? "–" : singlePer ? row.amount : `${formatRupees(row.amount)} / ${row.per}`,
        effective_from: formatDay(row.effective_from),
      }));
      const applied = {
        ...(filters.search.trim() ? { Search: filters.search.trim() } : {}),
        ...(filters.template ? { Template: filters.template } : {}),
        ...(filters.version ? { Version: filters.version } : {}),
      };
      const details = {
        "Report Name": `${component.name} - Employees`,
        Component: `${component.name} (${component.code})`,
        "Current Version": versionLabel(currentVersion(component).version),
        "Report Generated On": new Date().toLocaleString("en-GB"),
        "Overall Total Count": data.length,
      };
      const writer = format === "pdf" ? writers.downloadPdf : format === "csv" ? writers.downloadCsv : writers.downloadExcel;
      await writer(data, headers, `${component.code}_employees`, applied, details);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Mui.Tooltip
        title={busy ? "Downloading…" : rows.length ? "Download" : "No employees to download"}
        arrow
        slotProps={tooltipStyleProps}
      >
        <span style={{ display: "inline-flex" }}>
          <Mui.IconButton
            aria-label="Download"
            aria-haspopup="menu"
            aria-expanded={Boolean(anchor)}
            disabled={busy || !rows.length}
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{
              ...filterToolbarButtonSx,
              width: 40,
              height: 40,
              minHeight: 40,
              px: 0,
              border: `1px solid ${designColors.slate200}`,
              color: designColors.tealDark,
              "&.Mui-disabled": { color: designColors.slate400, borderColor: designColors.slate100 },
            }}
          >
            {busy ? <Mui.CircularProgress size={18} color="inherit" /> : <FileDownloadOutlined fontSize="small" />}
          </Mui.IconButton>
        </span>
      </Mui.Tooltip>
      <Mui.Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 180, borderRadius: 2, border: `1px solid ${designColors.slate200}`, boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)" } } }}
      >
        <Mui.Typography sx={{ ...designType.captionText, px: 2, pt: 1, pb: 0.5 }}>
          {rows.length.toLocaleString("en-IN")} employee{rows.length === 1 ? "" : "s"}
        </Mui.Typography>
        {EXPORT_FORMATS.map(({ value, label, icon: Icon }) => (
          <Mui.MenuItem key={value} onClick={() => download(value)} sx={{ fontSize: 14, gap: 1.25, py: 1 }}>
            <Icon sx={{ fontSize: 18, color: designColors.slate500 }} />
            {label}
          </Mui.MenuItem>
        ))}
      </Mui.Menu>
    </>
  );
};

const EmployeesTab = ({ component, filters, total }) => {
  const { search, setSearch, template, setTemplate, version, setVersion, page, setPage, rows } = filters;
  const from = total ? page * EMPLOYEES_PER_PAGE + 1 : 0;
  const to = Math.min(total, (page + 1) * EMPLOYEES_PER_PAGE);
  const lastPage = Math.max(0, Math.ceil(total / EMPLOYEES_PER_PAGE) - 1);
  const current = currentVersion(component).version;
  const versionCounts = component.versions
    .map((item) => ({ label: versionLabel(item.version), count: component.employees.filter((row) => row.version === item.version).length, current: item.version === current }))
    .filter((item) => item.count)
    .reverse();
  const customized = component.employees.filter((row) => row.customized).length;
  const pageRows = rows.slice(page * EMPLOYEES_PER_PAGE, (page + 1) * EMPLOYEES_PER_PAGE);
  const templateNames = [...new Set(component.employees.map((row) => row.template).filter(Boolean))];
  return (
    <Mui.Stack spacing={2}>
      <Mui.Box
        sx={{
          display: "grid",
          // Phone: search + download icon on the first row, the two pickers below.
          gridTemplateColumns: { xs: "minmax(0, 1fr) minmax(0, 1fr) auto", sm: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) auto" },
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Mui.Box sx={{ gridColumn: { xs: "1 / 3", sm: "auto" }, gridRow: { xs: 1, sm: "auto" } }}>
          <Mui.TextField size="small" fullWidth value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name or code" sx={searchFieldSx} />
        </Mui.Box>
        <SelectField value={template} onChange={setTemplate} options={toOptions(templateNames)} placeholder="All templates" />
        <SelectField value={version} onChange={setVersion} options={toOptions(component.versions.map((item) => versionLabel(item.version)).reverse())} placeholder="All versions" />
        <Mui.Box sx={{ gridColumn: { xs: 3, sm: "auto" }, gridRow: { xs: 1, sm: "auto" }, display: "flex" }}>
          <EmployeesDownload component={component} rows={rows} filters={filters} />
        </Mui.Box>
      </Mui.Box>
      {versionCounts.length || customized ? (
        <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {versionCounts.map((item) => (
            <CountPill key={item.label} label={`${item.count} on ${item.label}`} tone={item.current ? "green" : "slate"} />
          ))}
          {customized ? <CountPill label={`${customized} customized`} tone="purple" /> : null}
        </Mui.Stack>
      ) : null}
      {/* Full values, no truncation: scrolls sideways when wider than the panel. */}
      <SimpleTable
        fit
        heads={[
          { label: "Employee" },
          { label: "Template" },
          { label: "Version", width: 72 },
          { label: "Calculation" },
          { label: "Amount", align: "right", width: 96 },
          { label: "Effective From", width: 104 },
        ]}
      >
        {pageRows.length ? (
          pageRows.map((row) => (
            <Mui.TableRow key={row.id}>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{row.name || "–"}</Mui.Typography>
                <Mui.Typography sx={designType.captionText}>{[row.employee_code, row.department].filter(Boolean).join(" · ")}</Mui.Typography>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Stack spacing={0.5} alignItems="flex-start">
                  <span>{row.template || "–"}</span>
                  <ToneChip label={row.customized ? "Customized" : "Template Linked"} tone={row.customized ? "purple" : "slate"} />
                </Mui.Stack>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <VersionChip label={versionLabel(row.version)} current={row.version === current} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{row.calculation || "–"}</Mui.TableCell>
              <Mui.TableCell align="right" sx={tableCellSx}>
                <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{formatRupees(row.amount)}</Mui.Typography>
                <Mui.Typography sx={designType.captionText}>/ {row.per}</Mui.Typography>
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{formatDay(row.effective_from)}</Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={6} text={component.employees.length ? "No employees match these filters." : "No employee is paid this component yet."} />
        )}
      </SimpleTable>
      {total ? (
        <Mui.Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
          <Mui.Typography sx={designType.captionText}>Showing {from}–{to} of {total}</Mui.Typography>
          <Mui.Stack direction="row" spacing={1}>
            <SecondaryButton aria-label="Previous page" sx={{ minWidth: 42, px: 0 }} disabled={page === 0} onClick={() => setPage((value) => value - 1)}>
              <ChevronLeft />
            </SecondaryButton>
            <SecondaryButton aria-label="Next page" sx={{ minWidth: 42, px: 0 }} disabled={page >= lastPage} onClick={() => setPage((value) => value + 1)}>
              <ChevronRight />
            </SecondaryButton>
          </Mui.Stack>
        </Mui.Stack>
      ) : null}
      <RequestInfoBanner
        body={`Employees whose current salary includes ${component.code}. Amount is the full-period entitlement before payroll proration.`}
      />
    </Mui.Stack>
  );
};

// Activity Log tab: the shared Pay Inputs activity format (PayInputAuditTrail).
// The backend has no activity endpoint for salary components yet, so the
// entries are built from the version history (component-versions): one per
// version with what changed from the version before and its change note,
// plus the deactivation when the component is inactive. Newest first.
// Each version also carries what Version History used to show: Current /
// Superseded, its full calculation, who uses it and its amount at the
// sample GROSS.
const versionUsage = (component, number) => {
  const templates = component.templates.filter((item) => item.uses_version === number);
  if (!templates.length) return "No templates";
  const employees = templates.reduce((total, item) => total + (item.employees || 0), 0);
  return `${templates.length} template${templates.length === 1 ? "" : "s"} · ${employees.toLocaleString("en-IN")} employees`;
};

// Server notes like "Created" / "Component created." on V1.
const isCreatedNote = (note) => /^\s*(component\s+)?created\.?\s*$/i.test(String(note || ""));

const componentActivity = (component, components) => {
  const versions = [...component.versions].sort((a, b) => a.version - b.version);
  const latest = versions[versions.length - 1];
  const editable = isEditableMethod(latest?.method);
  const amountAt = (version) => {
    const value = previewVersion(version, components, PREVIEW_GROSS, component.code).amount;
    return value == null ? "–" : formatRupees(value);
  };
  const grossLabel = `At GROSS ${formatRupees(PREVIEW_GROSS)}`;
  const entries = versions.map((version, index) => {
    const previous = versions[index - 1];
    const isCurrent = version === latest;
    const amountChanged = editable && previous && amountAt(previous) !== amountAt(version);
    return {
      tags: [isCurrent ? { label: "Current", tone: "green" } : { label: "Superseded", tone: "slate" }],
      details: [
        { label: "Calculation", value: `${version.method} · ${describeCalculation(version)}` },
        ...(version.method === "Fixed Amount" ? [{ label: "Prorate", value: version.prorate ? "Yes" : "No" }] : []),
        ...(editable && !amountChanged ? [{ label: grossLabel, value: amountAt(version) }] : []),
        { label: "Used by", value: versionUsage(component, version.version) },
      ],
      id: `version-${version.version}`,
      type: previous ? "updated" : "created",
      title: previous ? `Saved as ${versionLabel(version.version)}` : `Created as ${versionLabel(version.version)}`,
      actor: { name: version.created_by || "System", role: "" },
      at: version.created_at,
      changes: previous
        ? [
            ...changedVersionFields(previous, version).map(({ key, label }) => ({
              label,
              from: versionFieldValue(previous, key),
              to: versionFieldValue(version, key),
            })),
            ...(amountChanged ? [{ label: grossLabel, from: amountAt(previous), to: amountAt(version) }] : []),
          ]
        : [],
      // A bare "Created" note only repeats the title.
      note: isCreatedNote(version.note) ? "" : version.note || "",
    };
  });
  if (!component.active && component.deactivation) {
    entries.push({
      id: "deactivated",
      type: "status",
      title: "Deactivated",
      actor: { name: component.deactivation.by || "System", role: "" },
      at: component.deactivation.at,
      changes: [{ label: "Status", from: "Active", to: "Inactive" }],
      note: component.deactivation.reason ? `Reason: ${component.deactivation.reason}` : "",
    });
  }
  return entries.reverse();
};

const ActivityTab = ({ component, components }) => (
  <Mui.Stack spacing={2}>
    <PayInputAuditTrail entries={componentActivity(component, components)} />
    <RequestInfoBanner body="Versions have no effective date. Payroll uses each employee's copied salary, so an old version stays valid for the templates and employees still on it." />
  </Mui.Stack>
);

// ---------------- View panel ----------------
// Same frame as the Pay Inputs views (Overtime): title + clickable
// breadcrumb, Edit as a header icon, a header card, full-width tabs,
// icon-labelled detail sections, and the record actions in the footer.

export const SalaryComponentViewPanel = ({
  open,
  component,
  components,
  canEdit,
  busy,
  detailsLoading,
  expanded,
  onToggleExpand,
  onClose,
  onEdit,
  onDeactivate,
  onReactivate,
  onDelete,
}) => {
  const history = useHistory();
  const [tab, setTab] = useState("details");
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [reason, setReason] = useState("");
  const id = component?.id;
  // Each component opens on its details, with nothing half-done.
  useEffect(() => {
    setTab("details");
    setDeactivateOpen(false);
    setReason("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const employeeFilters = useEmployeeFilters(component || { id: null, employees: [] });

  if (!component) return null;
  const status = componentStatus(component);
  const employees = employeeCount(component);
  const templates = templateCount(component);
  // System components can be edited too (same fields as Add); Deactivate /
  // Delete stay for user components.
  const canOpenEdit = canEdit && component.active;
  const editable = canOpenEdit && !component.system;

  const confirmDeactivate = async () => {
    if (!reason.trim()) return;
    // Stays open with the reason when the server refuses.
    if (!(await onDeactivate(component, reason.trim()))) return;
    setDeactivateOpen(false);
    setReason("");
  };

  // Record actions, full width like the Delete button on the Pay Inputs
  // views: Deactivate (and Delete while Unused), or Reactivate.
  let footer = null;
  if (editable) {
    footer = (
      <Mui.Stack direction="row" spacing={1.5} sx={{ width: "100%" }}>
        <Mui.Button
          variant="outlined"
          onClick={() => setDeactivateOpen(true)}
          disabled={busy}
          startIcon={<BlockOutlined sx={{ fontSize: 17 }} />}
          sx={{
            ...designShell.panelActionButton,
            flex: 1,
            minWidth: 0,
            minHeight: 42,
            color: designColors.slate700,
            borderColor: designColors.slate300,
            backgroundColor: designColors.white,
            "&:hover": { borderColor: designColors.slate400, backgroundColor: "#F8FAFC" },
          }}
        >
          Deactivate Component
        </Mui.Button>
        {/* Only an Unused component can be deleted; a used one is deactivated. */}
        {status === "Unused" && onDelete ? (
          <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
            <RequestCancelButton onClick={() => onDelete(component)} disabled={busy}>
              Delete Component
            </RequestCancelButton>
          </Mui.Box>
        ) : null}
      </Mui.Stack>
    );
  } else if (canEdit && !component.active) {
    footer = (
      <PrimaryButton onClick={() => onReactivate(component)} disabled={busy} startIcon={<RestartAltOutlined />} sx={{ width: "100%" }}>
        Reactivate Component
      </PrimaryButton>
    );
  }

  const total = employeeFilters.rows.length;

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={onClose}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Salary Component Details"
        subtitle="View the component's calculation, where it's used and its versions."
        // Payroll -> Payroll home; Salary Components -> back to the list.
        breadcrumb={[
          { label: "Payroll", onClick: () => history.push("/admin/payroll/setup") },
          "Salary",
          { label: "Salary Components", onClick: onClose },
          "Component Details",
        ]}
        bodySx={panelBodySx}
        headerActions={
          canOpenEdit ? (
            <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
              <Mui.IconButton
                size="small"
                aria-label="Edit"
                onClick={() => onEdit(component)}
                disabled={busy}
                sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </Mui.Tooltip>
          ) : null
        }
        footer={footer}
      >
        <Mui.Box sx={{ mb: 2 }}>
          <ComponentHeaderCard component={component} />
        </Mui.Box>
        <Mui.Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ ...designShell.panelTabs, mt: 0, mb: 2, borderBottom: `1px solid ${designColors.slate200}` }}
        >
          <Mui.Tab label="Details" value="details" />
          <Mui.Tab label={`Templates (${templates})`} value="templates" />
          <Mui.Tab label={`Employees (${employees})`} value="employees" />
          <Mui.Tab label="Activity Log" value="activity" />
        </Mui.Tabs>

        {tab === "details" ? <DetailsTab component={component} components={components} /> : null}
        {/* The other tabs come from their own APIs, loaded when the panel opens. */}
        {tab !== "details" && detailsLoading ? <Loader info="Loading..." minHeight="30vh" /> : null}
        {tab === "templates" && !detailsLoading ? <TemplatesTab component={component} /> : null}
        {tab === "employees" && !detailsLoading ? (
          <EmployeesTab component={component} filters={employeeFilters} total={total} />
        ) : null}
        {tab === "activity" && !detailsLoading ? <ActivityTab component={component} components={components} /> : null}
      </GlobalSidePanel>

      <GlobalConfirmDialog
        open={deactivateOpen}
        title={`Deactivate ${component.name}`}
        onCancel={() => {
          if (busy) return;
          setDeactivateOpen(false);
          setReason("");
        }}
        onConfirm={confirmDeactivate}
        confirmLabel="Deactivate"
        cancelLabel="Keep Active"
        loading={busy}
        loadingLabel="Deactivating..."
        maxWidth="sm"
        confirmDisabled={!reason.trim()}
        reasonValue={reason}
        onReasonChange={setReason}
        reasonPlaceholder="Why is this component being deactivated?"
        danger
      >
        {/* The shared dialog puts the reason box right under the text. */}
        <Mui.Box sx={{ mb: 2 }}>
          It's hidden from template pickers.{" "}
          {templates
            ? `The ${templates} template${templates === 1 ? "" : "s"} and ${employees} employees that have it keep it until they're revised.`
            : "No template uses it, so nobody is paid it today."}{" "}
          Code {component.code} stays reserved, and you can reactivate it any time.
        </Mui.Box>
      </GlobalConfirmDialog>
    </>
  );
};

// ---------------- Add / Edit panel ----------------

const VERSION_FIELD_LABELS = {
  category: "Category",
  method: "Method",
  amount: "Amount",
  percentage: "Percentage",
  base: "Of",
  formula: "Formula",
  prorate: "Prorate for paid days",
};

export const SalaryComponentFormPanel = ({
  open,
  mode,
  initial,
  component,
  components,
  categoryOptions = USER_CATEGORIES,
  methodOptions,
  baseOptions,
  busy,
  expanded,
  onToggleExpand,
  onClose,
  onSave,
  onTestFormula,
}) => {
  const history = useHistory();
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);
  // Errors the server sent back for a field (name / code checks).
  const [serverErrors, setServerErrors] = useState({});
  const [testGross, setTestGross] = useState(String(PREVIEW_GROSS));
  useEffect(() => {
    if (open) {
      setForm(initial);
      setShowErrors(false);
      setServerErrors({});
      setTestGross(String(PREVIEW_GROSS));
    }
  }, [open, initial]);

  const editing = mode === "edit" ? component : null;
  // System components edit with the same fields as Add (only Code is locked).
  const ownCode = editing?.code || form?.code.trim().toUpperCase();
  const codes = useMemo(() => formulaCodes(components, ownCode), [components, ownCode]);
  const draftVersion = form ? formToVersion(form) : null;
  const before = editing ? currentVersion(editing) : null;
  // By template count, not status: the server's status says "System" for a
  // system component even when templates use it.
  const used = Boolean(editing && templateCount(editing) > 0);
  const changes = editing && draftVersion ? changedVersionFields(before, draftVersion) : [];
  const newVersion = used && changes.length > 0;
  const nextLabel = before ? versionLabel(before.version + 1) : "V1";
  const errors = useMemo(
    () => (form ? validateComponentForm(form, { components, editing, needsNote: newVersion }) : {}),
    [form, components, editing, newVersion],
  );

  // Test box: once the formula passes the local check, the server checks it
  // the way a save would and works it out at the test GROSS.
  const [serverTest, setServerTest] = useState(null);
  const testFormula = form?.method === "Formula" ? form.formula.trim() : "";
  const testFormulaOk = Boolean(testFormula) && checkFormula(testFormula, codes, { ownCode, components }).valid;
  const testAt = Number(testGross) > 0 ? Number(testGross) : PREVIEW_GROSS;
  const testCategory = form?.category;
  useEffect(() => {
    setServerTest(null);
    if (!open || !onTestFormula || !testFormulaOk) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      onTestFormula({ category: testCategory, formula: testFormula, gross: testAt }, controller.signal)
        .then((value) => { if (!controller.signal.aborted) setServerTest(value); })
        .catch(() => {});
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, onTestFormula, testFormulaOk, testFormula, testAt, testCategory]);

  if (!form) return null;

  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
  };
  const error = (key) => serverErrors[key] || (showErrors ? errors[key] : undefined);
  const methods = methodOptions?.length
    ? EDITABLE_METHODS.filter((item) => methodOptions.includes(item.value))
    : EDITABLE_METHODS;
  // Percentage "Of": the server's list, else GROSS + the other earnings.
  const bases = baseOptions?.length ? baseOptions : [...percentBaseCodes(components, ownCode)].reverse();
  const was = (key) =>
    newVersion && changes.some((item) => item.key === key) ? `Was ${versionFieldValue(before, key)} in ${versionLabel(before.version)}` : undefined;
  const formulaCheck = form.method === "Formula" && form.formula.trim() ? checkFormula(form.formula, codes, { ownCode, components }) : null;
  const gross = Number(testGross) > 0 ? Number(testGross) : PREVIEW_GROSS;
  const calcReady = !errors.amount && !errors.percentage && !errors.base && !errors.formula;
  const preview = calcReady ? previewVersion(draftVersion, components, gross, ownCode) : null;
  const beforePreview = newVersion ? previewVersion(before, components, gross, ownCode) : null;
  const displayCode = ownCode || "Component";
  // The server result when it has one; nothing when it says the formula is wrong.
  const testAmount = serverTest?.valid === false ? null : serverTest?.amount ?? preview?.amount ?? null;

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    const result = await onSave(form, { newVersion });
    if (result?.fieldErrors) setServerErrors(result.fieldErrors);
  };

  const employees = editing ? employeeCount(editing) : 0;
  const subtitle = editing
    ? used
      ? `Used in ${templateCount(editing)} template${templateCount(editing) === 1 ? "" : "s"} · ${employees} employees`
      : "Not used by any template. Changes save in place."
    : "Saved as V1. Edit freely until a template uses it.";

  const textBox = (key, props = {}) => (
    <Mui.TextField size="small"
      value={form[key]}
      onChange={(event) => set(key)(event.target.value)}
      disabled={busy}
      error={Boolean(error(key))}
      sx={multilineFieldSx}
      {...props}
    />
  );

  const title = editing ? `Edit ${editing.name}` : "Add Component";
  const wasText = (key, text) =>
    text ? <Mui.Typography sx={{ ...designType.captionText, color: "#B54708", mt: 0.5, fontWeight: designWeights.semibold }}>{text}</Mui.Typography> : null;
  const categoryHelper = used
    ? "Changing the category saves a new version."
    : "Earnings count towards GROSS. Deductions are taken off the pay.";

  // Same layout as the Pay Inputs forms (Overtime): title + clickable
  // breadcrumb, white section cards with a one-line info note, and a short
  // info line under every field label. One field per row.
  return (
    <GlobalSidePanel
      open={open}
      onClose={busy ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={title}
      subtitle={subtitle}
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!busy) history.push("/admin/payroll/setup"); } },
        "Salary",
        { label: "Salary Components", onClick: () => { if (!busy) onClose(); } },
        title,
      ]}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={onClose}
          primaryLabel={busy ? "Saving..." : editing ? (newVersion ? `Save as ${nextLabel}` : "Save Changes") : "Save Component"}
          primaryProps={{ disabled: busy, onClick: save }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        {editing ? (
          newVersion ? (
            <RequestInfoBanner
              tone="warning"
              title={`You changed the calculation. Saving creates ${nextLabel}.`}
              body={`The ${templateCount(editing)} templates and ${employees} employees stay on ${versionLabel(before.version)} until they're upgraded.`}
            />
          ) : used ? (
            <RequestInfoBanner
              title={`Used in ${templateCount(editing)} template${templateCount(editing) === 1 ? "" : "s"}.`}
              body={`Name, payslip and description changes save in place. Changing the category, method, value or proration saves as ${nextLabel} with a change note.`}
            />
          ) : (
            <RequestInfoBanner title="Not used by any template." body="Every change, including the calculation, saves in place." />
          )
        ) : (
          <RequestInfoBanner
            title="The component is saved as V1."
            body="You can change it freely until a template uses it. After that, calculation changes save as a new version."
          />
        )}

        <FormSection title="Basic Information" info="How the component is named, coded and shown on the payslip.">
          <Field wide label={<>Component Name<Required /></>} helper="Shown in templates, salary details and reports." error={error("name")}>
            <TextField value={form.name} onChange={set("name")} disabled={busy} error={error("name")} placeholder="e.g. Night Shift Allowance" />
          </Field>
          {editing ? (
            <Field
              wide
              label={<Mui.Stack direction="row" spacing={0.5} alignItems="center" component="span"><span>Code</span><LockOutlined sx={{ fontSize: 14 }} /></Mui.Stack>}
              helper="Locked: formulas refer to it."
            >
              <Mui.TextField size="small" value={form.code} disabled sx={multilineFieldSx} />
            </Field>
          ) : (
            <Field wide label={<>Code<Required /></>} helper="2–5 characters, uppercase, starting with a letter. Can't be changed after saving." error={error("code")}>
              <Mui.TextField
                size="small"
                value={form.code}
                onChange={(event) => set("code")(event.target.value.toUpperCase().replace(/\s+/g, "_"))}
                disabled={busy}
                error={Boolean(error("code"))}
                placeholder="e.g. NSA"
                inputProps={{ maxLength: 20 }}
                sx={multilineFieldSx}
              />
            </Field>
          )}
          <Field wide label={<>Category<Required /></>} helper={categoryHelper} error={error("category")}>
            <SelectField value={form.category} onChange={set("category")} options={toOptions(categoryOptions)} disabled={busy} error={error("category")} placeholder="Select category" />
            {wasText("category", was("category"))}
          </Field>
          <Field wide label="Display in Payslip" helper="Show this line on the employee's payslip.">
            <YesNoSwitch checked={form.show_in_payslip} onChange={set("show_in_payslip")} disabled={busy} />
          </Field>
          {/* Only matters when the line is on the payslip. A name typed earlier
              is kept, so switching Display back on brings it back. */}
          {form.show_in_payslip ? (
            <Field wide label="Name in Payslip" helper="Optional. Blank uses the component name.">
              <TextField value={form.payslip_name} onChange={set("payslip_name")} disabled={busy} placeholder="Same as component name" />
            </Field>
          ) : null}
          <Field wide label="Description" helper="Optional. What the component is for.">
            {textBox("description", { multiline: true, minRows: 2, placeholder: "What is this component for?" })}
          </Field>
        </FormSection>

        <FormSection
          title={
            <Mui.Stack direction="row" spacing={1} alignItems="center" component="span" useFlexGap flexWrap="wrap">
              <span>Calculation</span>
              {newVersion ? (
                <ToneChip label={`${versionLabel(before.version)} → ${nextLabel}`} tone="teal" sx={{ fontWeight: designWeights.semibold }} />
              ) : (
                <VersionChip label={before ? versionLabel(before.version) : "V1"} current />
              )}
            </Mui.Stack>
          }
          info="How the amount is worked out every pay period."
        >
          <Field wide label={<>Calculation Method<Required /></>} helper="A fixed amount, a share of a base, or a formula." error={error("method")}>
            <MethodCards value={form.method} options={methods} onChange={set("method")} disabled={busy} />
            {wasText("method", was("method"))}
          </Field>
          {form.method === "Fixed Amount" ? (
            <Field wide label={<>Amount per Pay Period (₹)<Required /></>} helper="Paid every pay period, before proration." error={error("amount")}>
              <TextField value={form.amount} onChange={set("amount")} type="number" disabled={busy} error={error("amount")} placeholder="e.g. 1600" />
              {wasText("amount", was("amount"))}
            </Field>
          ) : null}
          {form.method === "Percentage" ? (
            <>
              <Field wide label={<>Percentage<Required /></>} helper="Share of the base, from 0 to 100." error={error("percentage")}>
                <TextField value={form.percentage} onChange={set("percentage")} type="number" disabled={busy} error={error("percentage")} placeholder="e.g. 40" />
                {wasText("percentage", was("percentage"))}
              </Field>
              <Field wide label={<>Of<Required /></>} helper="GROSS or other active earnings. Pick more than one to use their total." error={error("base")}>
                <CustomMultiSelectionWithLabel
                  value={baseCodes(form.base)}
                  onChange={(codes) => set("base")(joinBase(codes))}
                  options={[...new Set([...bases, ...baseCodes(form.base)])].map((code) => ({ label: code }))}
                  disabled={busy}
                  error={error("base")}
                  placeholder="Select base"
                  checkboxSx={bulkSelectionCheckboxSx}
                />
                {wasText("base", was("base"))}
              </Field>
            </>
          ) : null}
          {form.method === "Formula" ? (
            <Field
              wide
              label={<>Formula<Required /></>}
              helper={`Codes, GROSS, numbers, + − × ÷ %, brackets, > < = and AND / OR, and ${FORMULA_FUNCTIONS.join(", ")}. ROUND(x, 2) keeps 2 decimals.`}
              error={showErrors && !form.formula.trim() ? error("formula") : undefined}
            >
              <FormulaEditor
                value={form.formula}
                onChange={set("formula")}
                codes={codes}
                functions={FORMULA_FUNCTIONS}
                check={formulaCheck}
                error={error("formula")}
                disabled={busy}
              />
              {was("formula") ? wasText("formula", `Was ${before.formula} in ${versionLabel(before.version)}`) : null}
            </Field>
          ) : null}

          {form.method === "Formula" ? (
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <PreviewBox>
                <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                  <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>Test at GROSS</Mui.Typography>
                  <Mui.TextField
                    size="small"
                    type="number"
                    value={testGross}
                    onChange={(event) => setTestGross(event.target.value)}
                    InputProps={{ startAdornment: <Mui.InputAdornment position="start">₹</Mui.InputAdornment> }}
                    inputProps={{ "aria-label": "Test GROSS", min: 0, style: { textAlign: "right" } }}
                    sx={{ ...globalTextFieldSx, width: 170 }}
                  />
                </Mui.Stack>
                {preview ? (
                  <>
                    {preview.lines.map((line) => (
                      <PreviewLine key={line.label} label={line.label} value={line.value == null ? "–" : formatRupees(line.value)} />
                    ))}
                    <PreviewLine label={displayCode} value={testAmount == null ? "–" : formatRupees(testAmount)} strong divider />
                    {serverTest?.valid === false ? (
                      <Mui.Typography sx={{ ...designType.captionText, color: "#B42318", mt: 0.5 }}>
                        {serverTest.message || "The server couldn't work out this formula."}
                      </Mui.Typography>
                    ) : null}
                    {newVersion && beforePreview?.amount != null ? (
                      <Mui.Typography sx={{ ...designType.captionText, textAlign: "right" }}>
                        {versionLabel(before.version)} {formatRupees(beforePreview.amount)} → {nextLabel} {preview.amount == null ? "–" : formatRupees(preview.amount)}
                      </Mui.Typography>
                    ) : null}
                  </>
                ) : (
                  <Mui.Typography sx={designType.helperText}>Enter a valid formula to test it.</Mui.Typography>
                )}
              </PreviewBox>
            </Mui.Box>
          ) : null}

          {/* Prorate applies to Fixed Amount only. */}
          {form.method === "Fixed Amount" ? (
            <Field
              wide
              label="Prorate for Paid Days"
              helper={form.prorate ? "Yes: reduced for LOP and part periods." : "No: paid in full whenever the employee is paid."}
            >
              <YesNoSwitch checked={form.prorate} onChange={set("prorate")} disabled={busy} />
              {wasText("prorate", was("prorate"))}
            </Field>
          ) : null}

          {form.method !== "Formula" ? (
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <PreviewBox>
                <Mui.Stack direction="row" justifyContent="space-between" spacing={2} useFlexGap flexWrap="wrap">
                  <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>
                    Preview at GROSS <b>{formatRupees(PREVIEW_GROSS)}</b>
                  </Mui.Typography>
                  <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>
                    {newVersion && beforePreview ? (
                      <>
                        {versionLabel(before.version)} {beforePreview.amount == null ? "–" : formatRupees(beforePreview.amount)} → <b>{nextLabel} {preview?.amount == null ? "–" : formatRupees(preview.amount)}</b>
                      </>
                    ) : (
                      <>
                        {displayCode} = <b>{preview?.amount == null ? "–" : formatRupees(preview.amount)}</b>
                      </>
                    )}
                  </Mui.Typography>
                </Mui.Stack>
              </PreviewBox>
            </Mui.Box>
          ) : null}
        </FormSection>

        {newVersion ? (
          <FormSection
            title={`New Version · ${nextLabel}`}
            info={`What changes from ${versionLabel(before.version)}. Templates keep ${versionLabel(before.version)} until they're upgraded.`}
          >
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <DiffTable
                beforeLabel={`${versionLabel(before.version)} (current)`}
                afterLabel={`${nextLabel} (new)`}
                rows={changes.map((item) => ({
                  label: VERSION_FIELD_LABELS[item.key],
                  before: item.key === "percentage" ? describeCalculation(before) : versionFieldValue(before, item.key),
                  after: item.key === "percentage" ? describeCalculation(draftVersion) : versionFieldValue(draftVersion, item.key),
                  changed: true,
                }))}
              />
            </Mui.Box>
            <Field wide label={<>Change Note<Required /></>} helper="Why the calculation is changing. Shown in Version History." error={error("note")}>
              {textBox("note", { multiline: true, minRows: 2, placeholder: "Why is the calculation changing?" })}
            </Field>
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <RequestInfoBanner
                body={`After saving, the ${templateCount(editing)} templates show ${nextLabel} available. Upgrade a template to use ${nextLabel}, then move employees with a Template-Based Revision.`}
              />
            </Mui.Box>
          </FormSection>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
